import React from 'react';
import { Container, Wrapper } from 'src/components/Layout';
import classes from 'src/components/CmsPage/CmsAboutPage.scss';
import { LazyImg } from 'src/hooks/useLazyLoad';
import { yearsFromDateToNow } from 'src/util/yearsFromDateToNow';
import { Certificates } from 'src/components/Certificates/Certificates';
import { usePhoneNumber } from 'src/util/getPhoneNumber';
import { Link } from 'react-router';

type Props = Record<string, unknown>;

export const CmsAboutPage: React.FC<Props> = () => {
    const heroImage = require('../../../static/img/cms/about-us-header.jpg');
    return (
        <>
            <section className={classes.hero} style={{ backgroundImage: `url(${heroImage})` }} />
            <Wrapper variant="widget-wrapper">
                <Container>
                    <Intro />
                    <Stats />
                    <Grids />
                    <Videos />
                    <Cert />
                    <Info />
                </Container>
            </Wrapper>
        </>
    );
};

function Intro() {
    const yearsFromCompanyStartToNow = yearsFromDateToNow('01/01/1990');
    return (
        <div className={classes.intro}>
            <h1 className={classes.introTitle}>We are Workwear Express</h1>
            <p>
                <b>Outstanding service has always been at the heart of Workwear Express.</b>
            </p>
            <p>People and service. Those two things have defined the business since day one.</p>
            <p>
                In 1990, Workwear Express started with a van, some work clothing and a simple idea: businesses deserve
                quality gear, delivered with care. Thirty-five years on, it's the UK's largest customised workwear
                provider, serving more than a million clients in over 100 countries. A lot has changed. The principles
                haven't. Look after customers. Look after colleagues. The rest follows.
            </p>
            <p>
                At the centre of it all is Durham, home to Europe's largest customised workwear and fulfilment centre.
                It's 150,000 square feet of embroidery, print, transfer, warehousing and logistics under one roof. With
                further sites in Manchester, Leicester and the West Midlands, you get local care at national scale.
            </p>
            <p>
                But a building is just bricks and machines. The 300-plus people who work here are what make it work.
                They take pride in every garment that leaves the factory, whether it's one uniform for a local
                tradesperson or tens of thousands of items for one of the UK's biggest employers.
            </p>
            <p>
                Some of the country's largest and most complex brands trust Workwear Express. So do electricians,
                landscapers, cafés and charities. Every customer gets the same level of service, whatever their size.
                And every week, more companies choose Workwear Express than any other supplier in the industry. That's a
                trust the team works hard to keep.
            </p>
            <p>
                <b>Get in touch</b>
            </p>
            <p>
                Need help, have feedback or got an idea? <Link to="/contact-us">Contact us</Link>. Every message gets a
                reply. If something isn't right, we want to know. If there's a better way of doing things, we want to
                hear it. Praise, complaints, big ideas or small fixes, all welcome. Thank you for being part of the
                journey.
            </p>
            <p>Whether you've been with us for years or you're new here, we're here to help your business succeed.</p>
        </div>
    );
}

function Stats() {
    const yearsFromCompanyStartToNow = yearsFromDateToNow('01/01/1990');
    return (
        <div className={classes.stats}>
            <div className={classes.statsBlock}>
                <span className={classes.statsFigure}>2 million+</span>
                <span className={classes.statsDesc}>clients</span>
            </div>
            <div className={classes.statsBlock}>
                <span className={classes.statsFigure}>100+</span>
                <span className={classes.statsDesc}>countries served</span>
            </div>
            <div className={classes.statsBlock}>
                <span className={classes.statsFigure}>{yearsFromCompanyStartToNow}</span>
                <span className={classes.statsDesc}>years of expertise</span>
            </div>
        </div>
    );
}

function Grids() {
    return (
        <>
            <div className={[classes.grid, classes.gridTop].join(' ')}>
                <div className={[classes.gridImage, classes.gridImageTop].join(' ')}>
                    <LazyImg
                        src={require('../../../static/img/cms/about-us-1.jpg')}
                        alt="Workwear Express production facility"
                    />
                </div>
                <div className={[classes.gridImageDesk, classes.gridImageDeskTop].join(' ')}>
                    <LazyImg
                        src={require('../../../static/img/cms/about-us-2.jpg')}
                        alt="Workwear Express warehouse and fulfilment"
                    />
                </div>
                <div className={[classes.gridContent, classes.gridContentTop].join(' ')}>
                    <div className={classes.gridContentLogo}>
                        <LazyImg
                            src={require('../../../static/img/logo-footer-mobile.svg')}
                            alt="Workwear Express logo"
                        />
                    </div>
                    <h2 className={classes.gridContentTitle}>From One to One Million: Workwear at Scale</h2>
                    <p>
                        At Workwear Express, we deliver a complete end-to-end service for businesses of every size. Our
                        150,000 sq. ft production facilities in Durham bring embroidery, print, heat transfer and
                        garment finishing together under one roof, supported by fulfilment centres that ship thousands
                        of orders every day.
                    </p>
                    <p>
                        With more than <b>200,000 products available</b>, we provide everything from bespoke uniforms
                        and large-scale managed contracts to promotional clothing and one-off customised items.
                    </p>
                    <p>
                        This unique combination of UK manufacturing, advanced technology and nationwide distribution
                        means we can meet the scale that major brands demand while maintaining the personal service and
                        reliability that smaller businesses value.
                    </p>
                </div>
                <LazyImg
                    className={[classes.gridLogo, classes.gridLogoTop].join(' ')}
                    src={require('../../../static/img/logo-outline.svg')}
                    alt=""
                />
            </div>

            <div className={[classes.grid, classes.gridMiddle].join(' ')}>
                <div className={[classes.gridImage, classes.gridImageMiddle].join(' ')}>
                    <LazyImg
                        src={require('../../../static/img/cms/about-us-4.jpg')}
                        alt="Automated ordering and production technology"
                    />
                </div>
                <div className={[classes.gridImageDesk, classes.gridImageDeskMiddle].join(' ')}>
                    <LazyImg
                        src={require('../../../static/img/cms/about-us-8.jpg')}
                        alt="Customised workwear in production"
                    />
                </div>
                <div className={[classes.gridContent, classes.gridContentMiddle].join(' ')}>
                    <h2 className={classes.gridContentTitle}>Tech-driven Tailoring</h2>
                    <p>
                        At Workwear Express, technology drives every stage of our service. For businesses with large
                        numbers of wearers, we provide an industry-leading automated ordering portal, designed to make
                        uniform management simple, efficient and fully controlled. Linked directly to our 150,000 sq. ft
                        production facilities in Durham, the system connects ordering, customisation and fulfilment in
                        one seamless process.
                    </p>
                    <p>
                        With access to over 200,000 products, companies can manage complex uniform requirements across
                        multiple sites with ease, while ensuring brand consistency and cost control. From set
                        allocations and personalised wearer packs to secure next-day delivery, our technology ensures
                        the right clothing reaches the right person, every time.
                    </p>
                </div>
                <LazyImg
                    className={[classes.gridLogo, classes.gridLogoMiddle].join(' ')}
                    src={require('../../../static/img/logo-outline.svg')}
                    alt=""
                />
            </div>

            <div className={[classes.grid, classes.gridBottom].join(' ')}>
                <div className={[classes.gridImage, classes.gridImageBottom].join(' ')}>
                    <LazyImg
                        src={require('../../../static/img/cms/about-us-5.jpg')}
                        alt="Workwear Express colleagues at work"
                    />
                </div>
                <div className={[classes.gridImageDesk, classes.gridImageDeskBottom].join(' ')}>
                    <LazyImg
                        src={require('../../../static/img/cms/about-us-6.jpg')}
                        alt="Workwear Express office in Durham"
                    />
                </div>
                <div className={[classes.gridContent, classes.gridContentBottom].join(' ')}>
                    <h2 className={classes.gridContentTitle}>A Great Place to Work, Built for the Future</h2>
                    <p>
                        Behind every order, every garment and every delivery are our people. With more than 300
                        colleagues in Durham and across the UK, Workwear Express has been officially recognised as a
                        Great Place to Work in 2023, 2024 and 2025. That recognition reflects our culture: one built on
                        teamwork, development and pride in what we do. Our colleagues are at the centre of our success,
                        and their skills, commitment and innovation are what make our service stand out.
                    </p>
                    <p>
                        We’re equally committed to sustainability. As part of Bunzl plc, we are aligned with a clear,
                        science-based approach to reducing environmental impact. At Workwear Express, that means
                        investing in lower-impact fabrics, reducing waste in production, improving packaging and working
                        with our customers to design uniforms that last longer and perform better. The goal is simple:
                        to deliver outstanding service and quality clothing while doing the right thing for people and
                        the planet.
                    </p>
                </div>
                <LazyImg
                    className={[classes.gridLogo, classes.gridLogoBottom].join(' ')}
                    src={require('../../../static/img/logo-outline.svg')}
                    alt=""
                />
            </div>
        </>
    );
}

function Cert() {
    return (
        <div className={classes.cert}>
            <h2 className={classes.certTitle}>Our quality and ethical standards are officially recognised</h2>
            <Certificates />
        </div>
    );
}

const Videos = () => (
    <div className={classes.videoContainer}>
        <iframe
            src="https://www.youtube.com/embed/6WTtPSydnwE?si=e6teUHQs1VbFGjqa"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
        />
        <iframe
            src="https://www.youtube.com/embed/zy9AvSm8dNQ?si=9T8TuFlqLDJ1tFG-"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
        />
        <iframe
            src="https://www.youtube.com/embed/rXvJpTX5CUA"
            title="Workwear Express HQ Drone Flythrough"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
        />
        <iframe
            src="https://www.youtube.com/embed/tXFWlsqAZzM"
            title="Colleague Short Film: Anthony Ho"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
        />
        <iframe
            src="https://www.youtube.com/embed/WXK2YPVAwSg?si=EVM1n7YaPGheDA-S"
            title="Workwear Express 2023: A Record-Breaking Year with Even Bigger News Ahead"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
        />
        <iframe
            src="https://www.youtube.com/embed/gwvFPaAB9pg?si=z4yLCQpcQRAQNYfg"
            title="Colleague Short Film: Connor"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
        />
    </div>
);

function Info() {
    const phoneNumber = usePhoneNumber('spaced');
    return (
        <div className={classes.info}>
            <div className={classes.infoBlock}>
                <LazyImg src={require('../../../static/img/cms/about-us-7.jpg')} alt="Customer support team" />
                <div className={classes.infoContent}>
                    <h2 className={classes.infoTitle}>Here when you need us</h2>
                    <div className={classes.infoDetails}>
                        <div className={classes.infoDetailsItem}>
                            <h3>
                                <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
                            </h3>
                            <p>
                                Monday - Friday: 8am-6pm <br />
                                Saturday: 9am-1pm
                            </p>
                        </div>
                        <div className={classes.infoDetailsItem}>
                            <h3>workwearexpress.com</h3>
                            <p>
                                Live Chat available
                                <br />
                                Order online 24/7
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.infoBlock}>
                <LazyImg src={require('../../../static/img/cms/about-us-3.jpg')} alt="Join the Workwear Express team" />
                <div className={classes.infoContent}>
                    <h2 className={classes.infoTitle}>Work with us</h2>
                    <p className={classes.infoDesc}>There's always space for new talent on our team!</p>
                    <div className={classes.infoActions}>
                        <a
                            href="https://www.indeed.co.uk/cmp/Workwear-Express/jobs"
                            className="button"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Career Opportunities
                        </a>
                        <a
                            href="https://www.indeed.co.uk/cmp/Workwear-Express/jobs"
                            className="button button--bold"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Send us your CV
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}
